import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { matchPath, useHistory, useLocation, withRouter } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  CalendarIcon,
  ClientsIcon,
  DefaultText,
  FlexRow,
  generateImageUrl,
  IconButton,
  ImageLogo,
  MenuIcon,
  OverviewIcon,
  Spinner,
  Toolbar,
} from '@guryou/html-components';
import { sizes } from '@guryou/html-theme';
import { LOGOUT, stateIsLoaded, stateIsLoading, useCheckIsMobile, useCurrentUser } from '@guryou/redux';
import { Menu as MuiMenu, MenuItem as MuiMenuItem, Tooltip } from '@material-ui/core';
import Badge from '@material-ui/core/Badge';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder';
import ChatIcon from '@material-ui/icons/Chat';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck';
import logo from 'assets/img/logo.png';
import { DEFAULT_LANDING_PAGE, getLandingPage, LANDING_ELIGIBLE, setLandingPage } from 'utils/landingPage';

import AiChatModal from '../ai/AiChatModal';
import GyAiIcon from '../icons/GyAiIcon';

const getUnfinishedTasksCount = data => {
  const finishedTasksCount = Object.values(data).filter(value => value === true).length || 0;
  const total = 7;
  return total - finishedTasksCount;
};

const AdminHeader = ({ getCurrentPageTitle, providerTasks, providerInfo, taskCount, pendingCount }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const isItProvider = location?.pathname.includes('/providers/');
  const afterProvider = isItProvider ? location.pathname.split('/providers/')[1]?.split('/') : [];
  const providerId = afterProvider[0] || null;
  const currentSlug = afterProvider[1] || '';

  const [aiOpen, setAiOpen] = useState(false);
  const [landing, setLanding] = useState(getLandingPage());
  const canPinLanding = isItProvider && providerId && LANDING_ELIGIBLE.includes(currentSlug);
  const isDefaultLanding = landing === currentSlug;

  const toggleLanding = () => {
    const next = isDefaultLanding ? DEFAULT_LANDING_PAGE : currentSlug;
    setLandingPage(next);
    setLanding(next);
  };

  const quickNav = [
    { slug: 'calendar', title: 'Calendario', icon: <CalendarIcon className="w-5 h-5" /> },
    { slug: 'dashboard', title: 'Dashboard', icon: <OverviewIcon className="w-5 h-5" /> },
    { slug: 'chat', title: 'Chat', icon: <ChatIcon className="w-5 h-5 cursor-pointer" /> },
    { slug: 'customers', title: 'Clienti', icon: <ClientsIcon className="w-5 h-5 cursor-pointer" /> },
  ];

  return (
    <>
      <header className="bg-white border-b border-gray-200 z-50 h-20 relative">
        <nav className="flex items-center h-full px-6 gap-4">
          {/* Left zone — quick nav + page title */}
          <div className="flex items-center gap-4 flex-shrink-0">
            {isItProvider && providerId && (
              <div className="flex items-center space-x-2 mr-2 text-gray-500">
                {quickNav.map(n => (
                  <Tooltip key={n.slug} title={n.title}>
                    <button
                      onClick={() => history.push(`/providers/${providerId}/${n.slug}`)}
                      className="p-2 flex items-center justify-center hover:bg-gray-100 rounded-lg hover:text-gray-900 transition-colors"
                    >
                      {n.icon}
                    </button>
                  </Tooltip>
                ))}
                <div className="h-4 w-px bg-gray-300 ml-2"></div>
              </div>
            )}
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight whitespace-nowrap">{getCurrentPageTitle()}</h1>
          </div>

          {/* Center zone — page-specific filters & actions (portaled per page) */}
          <div className="flex items-center flex-1 justify-center gap-3 min-w-0 relative z-50 pointer-events-auto overflow-visible">
            <div id="admin-header-filters" className="flex items-center justify-center min-w-0 overflow-visible"></div>
            <div id="admin-header-actions" className="flex items-center gap-3 overflow-visible"></div>
          </div>

          {/* Right zone — always-on icons */}
          <div className="flex items-center gap-1 flex-shrink-0 text-gray-500">
            {isItProvider && providerId && (
              <Tooltip title={t('AI.assistant', 'AI Assistant')}>
                <button
                  onClick={() => setAiOpen(true)}
                  className="p-2 flex items-center justify-center hover:bg-gray-100 rounded-lg hover:text-gray-900 transition-colors"
                  aria-label="AI Assistant"
                >
                  <GyAiIcon size={22} />
                </button>
              </Tooltip>
            )}
            {canPinLanding && (
              <Tooltip
                title={
                  isDefaultLanding ? t('GENERAL.defaultLandingOn', 'Opens here on login') : t('GENERAL.defaultLandingSet', 'Set as page on login')
                }
              >
                <button
                  onClick={toggleLanding}
                  className={`p-2 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors ${
                    isDefaultLanding ? 'text-[#E6616A]' : 'hover:text-gray-900'
                  }`}
                  aria-label="Set as default page on login"
                >
                  {isDefaultLanding ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                </button>
              </Tooltip>
            )}
            {isItProvider && providerId && pendingCount > 0 && (
              <Tooltip title={`${pendingCount} ${t('PENDING_FLOWS.pendingApproval', 'pending approval')}`}>
                <button
                  onClick={() => history.push(`/providers/${providerId}/customers/pending-flows`)}
                  className="p-2 flex items-center justify-center hover:bg-gray-100 rounded-lg hover:text-gray-900 transition-colors"
                >
                  <Badge badgeContent={pendingCount} color="secondary">
                    <HourglassEmptyIcon />
                  </Badge>
                </button>
              </Tooltip>
            )}
            {stateIsLoaded(providerTasks) && isItProvider && providerId && (
              <Tooltip title="Provider progress">
                <button
                  onClick={() => history.push(`/providers/${providerId}/provider-progress`)}
                  className="p-2 flex items-center justify-center hover:bg-gray-100 rounded-lg hover:text-gray-900 transition-colors"
                >
                  <Badge badgeContent={taskCount} invisible={taskCount === 0} color="secondary">
                    <PlaylistAddCheckIcon />
                  </Badge>
                </button>
              </Tooltip>
            )}
          </div>
        </nav>
      </header>
      <AiChatModal open={aiOpen} onClose={() => setAiOpen(false)} />
    </>
  );
};

const Header = props => {
  const { t } = useTranslation();
  const { allRoutes } = props;

  const [anchorElMobile, setAnchorElMobile] = useState(null);
  const handleClickMobile = event => setAnchorElMobile(event.currentTarget);
  const handleCloseMobile = () => setAnchorElMobile(null);

  const isMobile = useCheckIsMobile();

  const location = useLocation();

  const isItDeal = location?.pathname.includes('/management/') || location?.pathname.includes('/providers/');
  const isAdmin = location?.pathname.includes('/admin');

  const changeAvatar = useSelector(state => state.user.changeAvatar);
  const providerInfo = useSelector(state => state.provider.generalInfo);
  const providerTasks = useSelector(state => state.providers.completedTasks);
  // const pendingCustomers = useSelector(state => state.approvedCustomers.checkPendingCustomers);
  const pendingFlowsList = useSelector(state => state.customersInvitationFlow.customersFlows);

  const { user } = useCurrentUser();
  const taskCount = getUnfinishedTasksCount(providerTasks?.data || {});
  const pendingCount = Array.isArray(pendingFlowsList?.data) ? pendingFlowsList.data.length : 0;

  const getCurrentPageTitle = () => {
    let matchedTitle = '';
    if (allRoutes) {
      const flattenedRoutes = React.Children.toArray(allRoutes);
      for (const routeEl of flattenedRoutes) {
        if (!routeEl || !routeEl.props) {
          continue;
        }
        const { path, exact, name } = routeEl.props;
        if (path) {
          const matchResult = matchPath(location.pathname, { path, exact: !!exact });
          if (matchResult) {
            matchedTitle = name;
            break;
          }
        }
      }
    }
    if (matchedTitle) {
      return matchedTitle;
    }

    const path = location.pathname.split('/').pop();

    if (isAdmin) {
      const titles = {
        dashboard: t('GENERAL.dashboard'),
        providers: t('PROVIDERS.title'),
        users: t('GENERAL.users'),
      };
      return titles[path] || t('GENERAL.admin');
    }

    if (location.pathname.includes('/management/')) {
      const titles = {
        dashboard: t('GENERAL.dashboard'),
        providers: t('PROVIDERS.titlePlural'),
        users: t('USERS.collabs'),
        'my-profile': t('PROFILE.title'),
        'affiliate-program': t('AFFILIATE.affiliate'),
        'my-memberships': t('MEMBERSHIPS.myMemberships'),
      };
      return titles[path] || t('GENERAL.dashboard');
    }

    if (location.pathname.includes('/providers/')) {
      const titles = {
        dashboard: t('GENERAL.dashboard'),
        calendar: t('CALENDAR.titlePlural'),
        customers: t('CUSTOMERS.title'),
        classes: t('CLASSES.title'),
        subscriptions: t('SUBSCRIPTIONS.subscriptions'),
        'payment-settings': t('PAYMENTS.payments'),
        gallery: t('GENERAL.gallery'),
        'email-marketing': t('MARKETING.emailMarketing'),
        reports: t('REPORTS.title'),
        settings: t('SETTINGS.title'),
        notifications: t('NOTIFICATIONS.title'),
      };
      if (titles[path]) {
        return titles[path];
      }

      // Fallback: capitalize strings, though untranslated
      return path.charAt(0).toUpperCase() + path.slice(1).replace(/-/g, ' ');
    }

    return '';
  };

  if (isAdmin || location.pathname.includes('/providers/') || location.pathname.includes('/management/')) {
    return (
      <AdminHeader
        getCurrentPageTitle={getCurrentPageTitle}
        providerTasks={providerTasks}
        providerInfo={providerInfo}
        taskCount={taskCount}
        pendingCount={pendingCount}
      />
    );
  }

  const isLoading = stateIsLoading(providerTasks) && stateIsLoading(pendingFlowsList);

  return (
    <AppBar color="inherit" height={sizes.appBarHeight}>
      <Toolbar>
        {user && providerInfo?.data && isMobile && <IconButton icon={MenuIcon} label={t('GENERAL.menu')} onClick={handleClickMobile} />}
        <ImageLogo src={logo} onClick={() => props.history.push('/management/providers')} />
        {user && (
          <FlexRow marginLeft="auto" marginRight={0} width="auto" alignItems="center" justifyContent="flex-end">
            {!isMobile &&
              (!user || stateIsLoading(changeAvatar) ? (
                <Spinner />
              ) : (
                <>
                  {!isLoading && providerInfo?.data ? (
                    <FlexRow xs={5} justifyContent="center">
                      <p className="font-extrabold mr-0 text-base mt-2.5 text-center">({providerInfo?.data?.timezone})</p>
                      <p className="text-xs text-gray-500 text-center">{t('GENERAL.timezone')}</p>
                    </FlexRow>
                  ) : null}
                  {!isLoading && providerInfo?.data ? (
                    <FlexRow xs={2}>
                      <IconButton
                        onClick={() => {
                          props.history.push(`/providers/${providerInfo.data?.id}/provider-progress`);
                        }}
                      >
                        <Badge badgeContent={taskCount} invisible={taskCount === 0} color="secondary">
                          <PlaylistAddCheckIcon />
                        </Badge>
                      </IconButton>
                    </FlexRow>
                  ) : null}
                  {!isLoading && providerInfo?.data ? (
                    <FlexRow xs={2}>
                      <IconButton
                        onClick={() => {
                          props.history.push(`/providers/${providerInfo.data?.id}/customers/pending-flows`);
                        }}
                        tooltip={t('PENDING_FLOWS.title')}
                      >
                        <Badge variant="dot" invisible={!pendingFlowsList?.data?.length} color="secondary">
                          <HourglassEmptyIcon />
                        </Badge>
                      </IconButton>
                    </FlexRow>
                  ) : null}
                  <Avatar imageSrc={generateImageUrl(user.image)} imageAlt="user-profile-photo" />
                </>
              ))}

            <MuiMenu
              anchorEl={anchorElMobile}
              open={!!anchorElMobile}
              onClose={handleCloseMobile}
              elevation={0}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {isItDeal && (
                <MuiMenuItem
                  onClick={() => {
                    props.history.push(`/providers/${providerInfo.data?.id}/calendar`);
                    handleCloseMobile();
                  }}
                >
                  <CalendarIcon />
                  <DefaultText marginLeft={1}>{t('CALENDAR.titlePlural')}</DefaultText>
                </MuiMenuItem>
              )}
              {isItDeal && (
                <MuiMenuItem
                  onClick={() => {
                    props.history.push(`/providers/${providerInfo.data?.id}/dashboard`);
                    handleCloseMobile();
                  }}
                >
                  <OverviewIcon />
                  <DefaultText marginLeft={1}>{t('GENERAL.dashboard').toUpperCase()}</DefaultText>
                </MuiMenuItem>
              )}
            </MuiMenu>
          </FlexRow>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default withRouter(Header);
